// ─── Cómo se reparten las sesiones de un día en la jornada (S6) ────────────
// La ÚNICA regla de reparto. La usan el generador del syllabus (servidor), la
// línea de tiempo del editor de plantillas y la vista previa del PDF: antes
// eran tres copias del mismo bucle, y nada garantizaba que siguieran iguales.
//
// La regla: los bloques de clase («section») de la jornada se llenan con las
// sesiones del día, en orden. Una sesión con duración propia ocupa esa
// duración; sin ella, ocupa lo que queda del bloque. Si la duración no cabe en
// lo que queda del bloque, la sesión se CORTA a lo que queda (recortada). Las
// sesiones que ya no encuentran bloque NO CABEN y no se crean.
//
// Caso real (SEBEX, 2026-10-17): 7 sesiones de 45 min en un sábado con bloques
// de 55, 60, 65 y 50 min (230 min de clase) → 45+10, 45+15, 45+20 y 45: tres
// sesiones recortadas a 10, 15 y 20 min, y sobran 5 min. El alumno veía esos
// fragmentos en su syllabus sin que nadie lo hubiera decidido.
//
// Nunca se redistribuye ni se fusiona nada: el contenido es del seminario. Lo
// que hace esta regla es decir, sin ambigüedad, qué pasa con cada sesión.
/** Una sesión recortada a MENOS de esta fracción de lo que pidió exige
 *  confirmación explícita para generar (decisión del PM, S6-c). Los recortes
 *  menores (p. ej. 56 → 55 min) solo avisan. */
export const MIN_SESSION_FRACTION = 0.5;
const minutes = (n) => {
    const v = Number(n);
    return Number.isFinite(v) && v > 0 ? v : 0;
};
/** La duración propia de una sesión: cualquier número ≥ 0 (también 0, como
 *  siempre lo hizo el generador); sin número válido, `null` = el resto del bloque. */
const ownMinutes = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
/** Reparte las sesiones de UN día en los bloques de su jornada. */
export function layoutDay(segments, sessions) {
    const timeline = [];
    const placed = [];
    let cursor = 0;
    let index = 0;
    let blockNumber = 0;
    let breakNumber = 0;
    let classMinutes = 0;
    let unusedMinutes = 0;
    for (const seg of segments) {
        const length = minutes(seg.durationMinutes);
        if (seg.kind === 'break') {
            breakNumber += 1;
            timeline.push({
                kind: 'break',
                breakNumber,
                label: seg.label,
                startMinute: cursor,
                endMinute: cursor + length,
            });
            cursor += length;
            continue;
        }
        blockNumber += 1;
        classMinutes += length;
        let remaining = length;
        while (remaining > 0 && index < sessions.length) {
            const session = sessions[index];
            const requestedMinutes = ownMinutes(session.durationMinutes);
            const actualMinutes = Math.min(requestedMinutes ?? remaining, remaining);
            const truncated = requestedMinutes !== null && actualMinutes < requestedMinutes;
            const entry = {
                session,
                index,
                blockNumber,
                startMinute: cursor,
                endMinute: cursor + actualMinutes,
                requestedMinutes,
                availableMinutes: remaining,
                actualMinutes,
                truncated,
                belowMinimum: truncated && actualMinutes < (requestedMinutes ?? 0) * MIN_SESSION_FRACTION,
            };
            placed.push(entry);
            timeline.push({ kind: 'session', ...entry });
            cursor += actualMinutes;
            remaining -= actualMinutes;
            index += 1;
        }
        // El final del bloque que ninguna sesión ocupó.
        cursor += remaining;
        unusedMinutes += remaining;
    }
    const unplaced = [];
    for (; index < sessions.length; index++) {
        unplaced.push({
            session: sessions[index],
            index,
            requestedMinutes: ownMinutes(sessions[index].durationMinutes),
        });
    }
    return { timeline, placed, unplaced, classMinutes, unusedMinutes };
}
export function dayFitSummary(layout) {
    const truncated = layout.placed.filter((p) => p.truncated);
    if (truncated.length === 0 && layout.unplaced.length === 0)
        return null;
    const all = [...layout.placed, ...layout.unplaced];
    const own = all.map((p) => p.requestedMinutes);
    const first = own[0];
    const eachMinutes = first !== null && first !== undefined && own.every((x) => x === first) ? first : null;
    const requestedMinutes = layout.placed.reduce((a, p) => a + (p.requestedMinutes ?? p.actualMinutes), 0) +
        layout.unplaced.reduce((a, p) => a + (p.requestedMinutes ?? 0), 0);
    return {
        sessions: all.length,
        eachMinutes,
        requestedMinutes,
        classMinutes: layout.classMinutes,
        truncatedTo: truncated.map((p) => p.actualMinutes),
        belowMinimum: truncated.filter((p) => p.belowMinimum).length,
        unplaced: layout.unplaced.length,
        unusedMinutes: layout.unusedMinutes,
    };
}
//# sourceMappingURL=session-layout.js.map